import { z } from "zod";
import { aiModeSchema, timelineActionSchema, timelineBlockSchema } from "./schemas.js";
/**
 * Contract for AI investment suggestions. The API builds a finance-context
 * object and calls the ai-platform gateway `/v1/structured` with this schema;
 * the gateway validates the model output against it before returning.
 */
export const RISK_LEVELS = ["low", "medium", "high"];
export const riskLevelSchema = z.enum(RISK_LEVELS);
export const investSuggestionSchema = z.object({
    instrument: z.string(),
    amountMinor: z.number().int().nonnegative(),
    riskLevel: riskLevelSchema,
    rationale: z.string(),
    horizonMonths: z.number().int().positive().optional(),
});
export const investAdviceSchema = z.object({
    safeToInvestMinor: z.number().int().nonnegative(),
    suggestions: z.array(investSuggestionSchema),
    notes: z.string(),
});
/** JSON Schema sent to the gateway's structured-output endpoint. */
export const investAdviceJsonSchema = {
    type: "object",
    additionalProperties: false,
    required: ["safeToInvestMinor", "suggestions", "notes"],
    properties: {
        safeToInvestMinor: { type: "integer", minimum: 0 },
        suggestions: {
            type: "array",
            items: {
                type: "object",
                additionalProperties: false,
                required: ["instrument", "amountMinor", "riskLevel", "rationale"],
                properties: {
                    instrument: { type: "string" },
                    amountMinor: { type: "integer", minimum: 0 },
                    riskLevel: { type: "string", enum: [...RISK_LEVELS] },
                    rationale: { type: "string" },
                    horizonMonths: { type: "integer", minimum: 1 },
                },
            },
        },
        notes: { type: "string" },
    },
};
export const AI_DISCLAIMER = "These are AI-generated suggestions for educational purposes only and are not financial advice. Consult a licensed advisor before investing.";
// ─── Finance Assistant (agentic SQL chat) ─────────────────────────────────────
/**
 * One step of the assistant's reasoning loop. The model either asks to run a
 * read-only SQL query against the user's own data (action="query") or returns
 * its final answer (action="final"). The agentic loop lives in the API; the
 * gateway only validates each step against this schema.
 */
export const assistantStepSchema = z.object({
    reasoning: z.string().optional(),
    action: z.enum(["query", "final"]),
    sql: z.string().optional(),
    answer: z.string().optional(),
});
export const assistantStepJsonSchema = {
    type: "object",
    additionalProperties: false,
    required: ["action"],
    properties: {
        reasoning: { type: "string", description: "Brief note on what you're doing and why." },
        action: {
            type: "string",
            enum: ["query", "final"],
            description: "'query' to run one read-only SQL SELECT, 'final' to answer the user.",
        },
        sql: {
            type: "string",
            description: "When action='query': a single read-only SELECT against the allowed views.",
        },
        answer: {
            type: "string",
            description: "When action='final': the natural-language answer for the user.",
        },
    },
};
export const ASSISTANT_DISCLAIMER = "I can read your Vouch data to answer questions, but I can't make changes. Figures are AI-generated summaries — double-check anything important.";
/**
 * Quick-command parsing. The API sends the user's free-text utterance plus a
 * compact catalogue of their account names and connection names to the gateway,
 * which returns a single structured intent. The API then validates confidence
 * and name resolution before executing (or returns a draft to the client).
 */
export const commandParseSchema = z.object({
    intent: z.enum(["income", "expense", "transfer", "loan", "repayment", "unknown"]),
    amountMinor: z.number().int().nonnegative().optional(),
    currency: z.string().optional(),
    accountName: z.string().optional(),
    toAccountName: z.string().optional(),
    counterpartyName: z.string().optional(),
    loanDirection: z.enum(["lent", "borrowed"]).optional(),
    description: z.string().optional(),
    // Date-only ISO value in the user's calendar, for example 2026-06-26.
    occurredAt: z.string().optional(),
    confidence: z.number().min(0).max(1),
    clarification: z.string().optional(),
});
export const commandParseJsonSchema = {
    type: "object",
    additionalProperties: false,
    required: ["intent", "confidence"],
    properties: {
        intent: {
            type: "string",
            enum: ["income", "expense", "transfer", "loan", "repayment", "unknown"],
            description: "income = money received into an account; expense = money spent from an account; transfer = move between the user's own two accounts; loan = money lent to or requested from a person; repayment = a payment against an existing loan; unknown = cannot tell.",
        },
        amountMinor: {
            type: "integer",
            description: "Amount in MINOR units (e.g. 1200.50 -> 120050). Omit if not stated.",
        },
        currency: { type: "string", description: "ISO code if stated or inferable (e.g. 'zloty'->PLN, 'K'/'12k' keeps currency of the account)." },
        accountName: { type: "string", description: "Source/affected account name, matched to one of the provided account names if possible." },
        toAccountName: { type: "string", description: "Destination account name for a transfer between the user's own accounts." },
        counterpartyName: { type: "string", description: "Person/connection name for a loan or person-to-person send, matched to a provided connection name if possible." },
        loanDirection: { type: "string", enum: ["lent", "borrowed"], description: "For intent='loan': lent when the user is sending/lending money; borrowed when the user asks to receive/borrow money." },
        description: { type: "string", description: "Short human label for the entry (e.g. 'Coffee', 'Salary')." },
        occurredAt: {
            type: "string",
            description: "Date the money event happened, as YYYY-MM-DD. Resolve phrases such as yesterday, 26th June, and June 26 using the user's current local date.",
        },
        confidence: { type: "number", description: "0..1 confidence that the parse is correct and actionable." },
        clarification: { type: "string", description: "If unknown/low-confidence, a short question the assistant would ask." },
    },
};
/** Request body for the quick-command endpoint. */
export const commandInputSchema = z.object({
    text: z.string().min(1).max(500),
    mode: aiModeSchema.optional(),
    timezone: z.string().trim().min(1).max(80).optional(),
});
// ─── V2 AI: renderable answer + streaming (ai-platform domain) ────────────────
/**
 * Provenance for a grounded AI answer: which domain tools ran, the gateway run
 * id, and opaque references to the results the answer was synthesised from.
 */
export const aiProvenanceSchema = z.object({
    toolsUsed: z.array(z.string()).default([]),
    runId: z.string().optional(),
    resultRefs: z.array(z.string()).optional(),
});
/**
 * A fully-realised AI answer the clients can render: markdown/plain `text`, plus
 * renderable `blocks` (charts/tables/images/summaries) and suggested follow-up
 * `actions`, and where the numbers came from. Reuses the timeline block/action
 * contract so Android/web render AI answers with the same components as timelines.
 */
export const aiUiResponseSchema = z.object({
    text: z.string(),
    blocks: z.array(timelineBlockSchema).default([]),
    suggestions: z.array(timelineActionSchema).default([]),
    provenance: aiProvenanceSchema.default({ toolsUsed: [] }),
});
export const aiStreamFrameSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("status"), label: z.string() }),
    z.object({ kind: z.literal("tool_start"), tool: z.string(), index: z.number().int() }),
    z.object({
        kind: z.literal("tool_end"),
        tool: z.string(),
        index: z.number().int(),
        ok: z.boolean(),
        error: z.string().optional(),
    }),
    z.object({ kind: z.literal("text"), chunk: z.string() }),
    z.object({
        kind: z.literal("draft"),
        draftId: z.string(),
        summary: z.string(),
        tool: z.string(),
        expiresAt: z.string().optional(),
    }),
    z.object({ kind: z.literal("done"), conversationId: z.string(), response: aiUiResponseSchema }),
    z.object({ kind: z.literal("error"), message: z.string() }),
]);
//# sourceMappingURL=ai.js.map